import React, { useState } from 'react'
import { Download, ChevronDown, FileSpreadsheet, FileDown, FileType, Loader2 } from 'lucide-react'
import { toast } from 'react-hot-toast'
import useStore from '../store/useStore'
import { cachedApiService } from '../services/api'

const ExportButton = ({ totalRows = 0, sortField = null, sortDirection = 'asc' }) => {
  const [showMenu, setShowMenu] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportFormat, setExportFormat] = useState(null)
  const { filters } = useStore()

  const handleExport = async (format) => {
    setShowMenu(false)
    if (exporting) return
    setExporting(true)
    setExportFormat(format)
    const rowCount = totalRows > 0 ? totalRows.toLocaleString() : 'toutes les'
    const toastId = toast.loading(
      `Export ${format === 'excel' ? 'Excel' : format === 'pdf' ? 'PDF (Rapport)' : 'CSV'} en cours… (${rowCount} lignes)`,
      { duration: Infinity }
    )
    try {
      await cachedApiService.exportData(filters, format, sortField, sortDirection)
      toast.success(
        `Export terminé : ${rowCount} lignes exportées`,
        { id: toastId, duration: 4000 }
      )
    } catch (err) {
      console.error('Export error:', err)
      const msg = err?.response?.status === 404
        ? 'Aucune donnée à exporter pour ces filtres'
        : `Erreur export: ${err.message || 'échec'}`
      toast.error(msg, { id: toastId, duration: 5000 })
    } finally {
      setExporting(false)
      setExportFormat(null)
    }
  }

  const formats = [
    { id: 'pdf', label: 'PDF (Rapport)', desc: 'KPI + graphiques + données', icon: FileType, color: 'red', hoverBg: 'hover:bg-red-50 dark:hover:bg-red-900/20', iconColor: 'text-red-600' },
    { id: 'excel', label: 'Excel (.xlsx)', desc: 'Formaté, largeurs auto', icon: FileSpreadsheet, color: 'emerald', hoverBg: 'hover:bg-emerald-50 dark:hover:bg-emerald-900/20', iconColor: 'text-emerald-600' },
    { id: 'csv', label: 'CSV (;)', desc: 'UTF-8, séparateur point-virgule', icon: FileDown, color: 'blue', hoverBg: 'hover:bg-blue-50 dark:hover:bg-blue-900/20', iconColor: 'text-blue-600' },
  ]

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        disabled={exporting}
        className="inline-flex items-center gap-2 px-3.5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium shadow-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed active:scale-95"
        title="Exporter les données"
      >
        {exporting ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Download className="w-4 h-4" />
        )}
        <span>{exporting ? `Export ${exportFormat?.toUpperCase()}…` : 'Exporter'}</span>
        {!exporting && <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {showMenu && !exporting && (
        <>
          {/* Click-outside overlay */}
          <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
          <div className="absolute right-0 mt-2 w-60 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-20 overflow-hidden">
            <div className="p-2 border-b border-gray-100 dark:border-gray-800">
              <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-2 py-1">
                {totalRows > 0
                  ? `Exporter ${totalRows.toLocaleString()} lignes`
                  : 'Exporter les données filtrées'}
              </p>
            </div>
            <div className="p-1.5">
              {formats.map(fmt => {
                const Icon = fmt.icon
                return (
                  <button
                    key={fmt.id}
                    onClick={() => handleExport(fmt.id)}
                    className={`w-full flex items-center gap-3 px-2.5 py-2.5 text-sm rounded-lg text-gray-700 dark:text-gray-300 ${fmt.hoverBg} transition-colors`}
                  >
                    <Icon className={`w-5 h-5 ${fmt.iconColor} flex-shrink-0`} />
                    <div className="text-left">
                      <div className="font-medium text-sm">{fmt.label}</div>
                      <div className="text-[11px] text-gray-400 dark:text-gray-500">{fmt.desc}</div>
                    </div>
                  </button>
                )
              })}
            </div>
            <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <p className="text-[10px] text-gray-400 dark:text-gray-500">
                L'export contient toutes les lignes correspondant aux filtres actifs.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default ExportButton
